import { provideZoneChangeDetection, NgModule } from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
  HttpClientModule,
} from '@angular/common/http';
import { HttpErrorInterceptor } from '@core/interceptors/http-error.interceptor';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ReadableFallbackMissingTranslationHandler } from '@core/readable-fallback-missing-translation.handler';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthorizeInterceptor } from '@core/interceptors/authorize.interceptor';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { firmwareReducer } from './store/reducers/firmware.reducer';
import * as FirmwareEffects from './store/effects/firmware.effects';

// Required for Angular multi-browser support
import { BrowserModule } from '@angular/platform-browser';

// Required modules and components for this application
import { AppRoutingModule, routes } from './app-routing.module';
import { AppComponent } from './app.component';

// Required for MSAL
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import { environment } from '@root/environments/environment';
import { AuthStore } from './store/auth.store';
import { MSAL_SCOPES } from '@core/constants';
import { provideAuthInitializer } from '@core/auth-initializer';
import { provideTranslationInitializer } from '@core/translation-initializer';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import { ErrorHandler } from '@angular/core';
import { ApplicationInsightsErrorHandler } from '@core/services/application-insights-error-handler.service';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: '09d9bc85-85ec-4260-bca0-ccd37aae465a',
      authority: 'https://login.microsoftonline.com/0918d443-55b4-4d09-b8db-b43250722a11',
      redirectUri: `${window.location.origin}/auth-callback/`,
      postLogoutRedirectUri: `${window.location.origin}/auth/login`,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
  });
}

// MSAL Interceptor is required to request access tokens in order to access the protected resource (Graph)
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.api.url, MSAL_SCOPES);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

// MSAL Guard is required to protect routes and require authentication before accessing protected routes
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: MSAL_SCOPES,
    },
  };
}

// Create an NgModule that contains the routes and MSAL configurations
@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    MsalModule,
    SpinnerComponent,
    StoreModule.forRoot({ firmware: firmwareReducer }),
    EffectsModule.forRoot(FirmwareEffects),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: false,
    }),
  ],
  providers: [
    AuthStore,
    provideAuthInitializer(),
    provideTranslationInitializer(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthorizeInterceptor, multi: true },
    provideCharts(withDefaultRegisterables()),
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: ReadableFallbackMissingTranslationHandler,
      },
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }).providers!,
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: 'IMAGE_CACHE',
      useValue: {},
    },
    {
      // Focus the first input on dialog open instead of the first tabbable element (the
      // dialog-shell close button), which otherwise shows its focus state-layer on open.
      //
      // Spread over a real MatDialogConfig rather than a bare literal: MatDialog.open() merges
      // these options as `{ ...(defaultOptions || new MatDialogConfig()), ...config }`, so a
      // literal here means the class defaults are never constructed at all. Everything this
      // object does not name - role, ariaModal, hasBackdrop, restoreFocus - would end up
      // undefined instead of falling back, for every dialog in the app.
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { ...new MatDialogConfig(), autoFocus: 'input' },
    },
    {
      provide: ErrorHandler,
      useClass: ApplicationInsightsErrorHandler,
    },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
